/**
 * Resolve the date to use for a quote's fuel-surcharge (FSC) calculation, per
 * the quote's fuel-calc basis (masterquote.fuelcalctype). The FSC % is
 * date-dependent (tariff bands), so the invoice must recompute it as of the
 * right operational date. If the relevant milestone hasn't happened yet (e.g.
 * DELIVERY DATE on an undelivered order), fall back to today.
 *
 * Pure + `today`-injected so it's deterministic under test.
 */

export interface FscDateCandidates {
  /** Delivery leg actual arrival date (null when not delivered). */
  delivery?: Date | null;
  /** Pickup/originate leg actual date (null when not picked up). */
  pickup?: Date | null;
  /** Terminate leg actual date (null when not terminated). */
  terminate?: Date | null;
  /** Order create date. */
  create?: Date | null;
}

export interface FscDateResult {
  date: Date;
  /** The normalized basis that was applied. */
  basis: string;
  /** True when the basis' milestone hadn't happened and we fell back to today. */
  usedFallback: boolean;
}

const isValidDate = (d: Date | null | undefined): d is Date =>
  d instanceof Date && !Number.isNaN(d.getTime());

/** Resolve the FSC calculation date for `fuelCalc`, falling back to `today`. */
export function resolveFscDate(
  fuelCalc: string,
  candidates: FscDateCandidates,
  today: Date,
): FscDateResult {
  const basis = (fuelCalc || '').trim().toUpperCase();
  const pick = (c: Date | null | undefined): FscDateResult =>
    isValidDate(c)
      ? { date: c, basis, usedFallback: false }
      : { date: today, basis, usedFallback: true };

  switch (basis) {
    case 'DELIVERY DATE':
      return pick(candidates.delivery);
    case 'PICKUP DATE':
      return pick(candidates.pickup);
    case 'TERMINATE DATE':
      return pick(candidates.terminate);
    case 'CREATE DATE':
      return pick(candidates.create);
    case 'CURRENT DATE':
    default:
      // Today is the intended date here, not a fallback.
      return { date: today, basis, usedFallback: false };
  }
}

/** Format a Date as YYYY-MM-DD in local time (for the /quotes/fsc as_of param). */
export function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
